import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../store';

const useDebouncedSelector = <TSelected = unknown>(
  selector: (state: RootState) => TSelected,
  time = 250,
  equalityFn?: (left: TSelected | undefined, right: TSelected) => boolean
): TSelected => {
  const selectorData = useSelector(selector);
  const result = useRef<TSelected>();
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const [data, setData] = useState(selectorData);

  if (timeout.current) clearTimeout(timeout.current);

  useEffect(() => () => timeout.current && clearTimeout(timeout.current), []);

  if (time === 0) return selectorData;

  timeout.current = setTimeout(() => {
    if (equalityFn ? !equalityFn(result.current, selectorData) : result.current !== selectorData) {
      result.current = selectorData;
      setData(selectorData);
    }
  }, time);

  return data;
};

export default useDebouncedSelector;
